import React, { useState, useRef } from 'react'
import { Image } from 'react-datocms'
import styled from '@emotion/styled'
import useResizeObserver from 'use-resize-observer'
import { saveAs } from 'file-saver'
import { useIntl } from 'react-intl'

import Slider from '../Slider'
import { mediaHover } from '../../styles/mq'

const Wrapper = styled.div`
  position: relative;
  :before {
    content: '';
    display: block;
    padding-top: 56.25%;
  }
`

const Slide = styled.div`
  margin: 0 var(--space-smaller);
  cursor: pointer;
  height: 100%;
  max-width: 100%;
  img {
    pointer-events: none;
  }
`

const StyledSlider = styled(Slider)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`

const DownloadableImage = styled.div`
  position: relative;
  display: flex;
  height: 100%;
  align-items: center;
  > .download {
    opacity: 0;
    transition: opacity 350ms ease;
    position: absolute;
    color: var(--color-white);
    bottom: var(--space-small);
    right: var(--space-small);
    font-size: var(--font-size-smallest);
    text-transform: uppercase;
    letter-spacing: var(--letter-spacing-uppercase);
    display: none;
    ${mediaHover} {
      display: block;
    }
  }
  ${mediaHover} {
    :hover {
      > .download {
        opacity: 0.5;
        &:hover {
          opacity: 1;
        }
      }
    }
  }
`

const calcWidth = (width, ratio) => {
  return width
    ? (width / 16) * 9 * Math.min(ratio, 1.5)
    : (80 / 16) * 9 * ratio + 'vw'
}

const GalleryImage = ({ data, downloadable, url, filename }) => {
  const { formatMessage: t } = useIntl()
  return downloadable ? (
    <DownloadableImage>
      <Image data={data} />
      <div className="download" onClick={() => saveAs(url, filename)}>
        ↓ {t({ id: 'download image' })}
      </div>
    </DownloadableImage>
  ) : (
    <Image data={data} />
  )
}

const GalleryBlock = React.memo(({ images, downloadable }) => {
  const [index, setIndex] = useState(0)
  const ref = useRef(null)
  const { width } = useResizeObserver({ ref })

  return (
    <Wrapper ref={ref} className="block c-xlarge">
      <StyledSlider index={index} onIndexChange={setIndex}>
        {images.map(({ id, fluidFullWidth, url, filename }) => (
          <Slide
            key={id}
            style={{ width: calcWidth(width, fluidFullWidth.aspectRatio) }}
          >
            <GalleryImage
              data={fluidFullWidth}
              url={url}
              filename={filename}
              downloadable={downloadable}
            />
          </Slide>
        ))}
      </StyledSlider>
    </Wrapper>
  )
})

export default GalleryBlock
